import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { addDoc, collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../firebase";
import { MP_AMBIENTE, cancelarPixConsignado, concluirConexao, desconectarMercadoPagoLoja, gerarPixConsignado, iniciarConexao, mensagemMP, mpHabilitado, novoRequestId, retornoPendente, verificarPixConsignado } from "../utils/mercadoPago";
import { C } from "../constants/theme";
import { Btn, Card } from "./ui";
import { HeaderIcon, LogoutButton } from "./AppHeader";
import { CalcIcon } from "./calculadora/CalcIcon";
import { ToolButton, ToolDialog, ToolEmpty, ToolSection, ToolFilters, ToolSearch } from "./ToolWorkspace";
import { Inp } from "./ui";
import { gerarPixPayload, normalizarChavePix, tipoChavePix } from "../utils/pix";
import "./painelLojaParceira.css";
import { ToastContainer } from "./ui/ToastContainer";
import { toast } from "../hooks/useToast";
import { centavos, precoDaLoja, resumoLoja, vendasNoPeriodo } from "../utils/consignacao";
import { CommerceTabs, CommercePanel } from "./commerce/CommerceDialog";
import { gatewayPorId, estadoDoGateway } from "./configuracoes/pagamentos/gateways";
import { FluxoConexao } from "./configuracoes/pagamentos/FluxoConexao";
import { GerenciarGateway } from "./configuracoes/pagamentos/GerenciarGateway";
import { AlertasMercadoPago } from "./configuracoes/AlertasMercadoPago";
import "./configuracoes/pagamentos/pagamentos.css";

/* App da loja parceira (consignação): sem plano, só PDV, estoque, contas e
   configurações. Ela vê as peças que o dono deixou, vende pelo preço dela
   (até o máximo do dono) e acompanha quanto precisa repassar. */

const registrarVenda = dados => httpsCallable(getFunctions(undefined, "us-central1"), "registrarVendaConsignada")(dados);
const brl = v => "R$ " + centavos(v).toFixed(2).replace(".", ",");
const quando = v => v?.toDate?.().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) || "agora";
const PAGAMENTOS = [["dinheiro", "Dinheiro"], ["pix", "PIX"], ["cartao", "Cartão"]];
const num = v => parseFloat(String(v ?? "").replace(",", ".")) || 0;

// Etapa de pagamento: troco no dinheiro, QR Code do Pix da loja e, em todos,
// um "Confirmar pagamento" antes de registrar a venda.
function Pagamento({ forma, total, loja, enviando, onConfirmar, onClose, mp = false, itensVenda = [], onPagoMp }) {
  const [recebido, setRecebido] = useState("");
  const [qr, setQr] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [emailCliente, setEmailCliente] = useState("");
  const [mpPix, setMpPix] = useState(null);
  const [gerando, setGerando] = useState(false);
  const chave = loja?.pixLoja?.chave;
  const usaMp = forma === "pix" && mp;
  const pix = mpPix?.pix?.codigo || (forma === "pix" && !mp && chave ? gerarPixPayload({ chave, nome: loja.pixLoja.nome || loja.nome, cidade: loja.pixLoja.cidade, valor: total }) : "");
  // Pix do Mercado Pago da loja: confere a cada 5 s; quando paga, o servidor já registrou a venda
  useEffect(() => {
    if (!mpPix?.cobrancaId) return;
    const t = setInterval(() => verificarPixConsignado(mpPix.cobrancaId).then(r => { if (r.situacao === "pago") { clearInterval(t); onPagoMp?.(); } }).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [mpPix?.cobrancaId, onPagoMp]);
  const gerarMp = async () => {
    setGerando(true);
    try { setMpPix(await gerarPixConsignado({ lojaId: loja.id, itens: itensVenda, email: emailCliente, requestId: novoRequestId() })); }
    catch (e) { toast(mensagemMP(e), "error"); }
    setGerando(false);
  };
  // Fechar sem pagar cancela o Pix no Mercado Pago (se já tinha sido pago, conta como venda)
  const fechar = () => {
    if (mpPix?.cobrancaId) cancelarPixConsignado(mpPix.cobrancaId).then(r => { if (r.situacao === "pago") onPagoMp?.(); }).catch(() => {});
    onClose();
  };
  useEffect(() => {
    if (!pix) return;
    let vivo = true;
    import("qrcode").then(Q => Q.toDataURL(pix, { width: 240, margin: 2, color: { dark: "#000", light: "#fff" } })).then(url => vivo && setQr(url)).catch(() => {});
    return () => { vivo = false; };
  }, [pix]);
  const valorRecebido = num(recebido), falta = !!recebido && valorRecebido < total;
  const copiar = () => navigator.clipboard?.writeText(pix).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2500); }).catch(() => {});
  const titulo = { dinheiro: "Pagamento em dinheiro", pix: "Pagamento por Pix", cartao: "Pagamento no cartão" }[forma];

  return (
    <ToolDialog eyebrow="SUA LOJA" title={titulo} subtitle={"Total da venda: " + brl(total)} icon="notas" color="#71e5ce" onClose={fechar} busy={enviando || gerando} compact>
      <div className="partner-payment">
        {forma === "dinheiro" && <>
          <label className="partner-payment-field">Valor recebido
            <input inputMode="decimal" autoFocus placeholder={total.toFixed(2).replace(".", ",")} value={recebido} onChange={e => setRecebido(e.target.value)} />
          </label>
          <div className="partner-payment-quick">{[5, 10, 20, 50, 100].map(v => <button key={v} type="button" onClick={() => setRecebido(String(v))}>R$ {v}</button>)}</div>
          <div className="partner-payment-change" aria-live="polite">
            <span>Troco</span>
            <strong style={{ color: falta ? C.danger : C.success }}>{recebido ? brl(Math.max(0, valorRecebido - total)) : "R$ —"}</strong>
            {falta && <small>Faltam {brl(total - valorRecebido)}</small>}
          </div>
        </>}
        {usaMp && (mpPix ? <>
          <div className="partner-payment-qr">{qr ? <img src={qr} alt={"QR Code Pix de " + brl(total)} /> : <span>Gerando QR Code…</span>}</div>
          <ToolButton secondary onClick={copiar}>{copiado ? "Código copiado!" : "Copiar Pix copia e cola"}</ToolButton>
          <p className="partner-payment-note" aria-live="polite">Aguardando o pagamento pelo Mercado Pago… Quando o Pix cair, a venda é registrada sozinha.</p>
        </> : <>
          <label className="partner-payment-field">E-mail do cliente (o Mercado Pago pede)
            <input type="email" autoComplete="off" value={emailCliente} onChange={e => setEmailCliente(e.target.value)} placeholder="cliente@email.com" style={{ fontSize: 16, fontWeight: 600 }} />
          </label>
          <ToolButton disabled={gerando || !emailCliente.includes("@")} onClick={gerarMp}>{gerando ? "Gerando Pix…" : "Gerar Pix de " + brl(total) + " (Mercado Pago)"}</ToolButton>
        </>)}
        {forma === "pix" && !mp && (pix ? <>
          <div className="partner-payment-qr">{qr ? <img src={qr} alt={"QR Code Pix de " + brl(total)} /> : <span>Gerando QR Code…</span>}</div>
          <ToolButton secondary onClick={copiar}>{copiado ? "Código copiado!" : "Copiar Pix copia e cola"}</ToolButton>
          <p className="partner-payment-note">Recebedor: {loja.pixLoja.nome || loja.nome}. Confira no app do banco se o valor caiu antes de confirmar.</p>
        </> : <p className="partner-payment-note">Cadastre a chave Pix da loja em Configurações para mostrar o QR Code aqui. Recebeu por Pix mesmo assim? Confirme abaixo.</p>)}
        {forma === "cartao" && <p className="partner-payment-note">Passe {brl(total)} na maquininha e confirme quando o pagamento for aprovado.</p>}
        {!usaMp && <ToolButton disabled={enviando || falta} onClick={onConfirmar}>{enviando ? "Registrando venda…" : "Confirmar pagamento de " + brl(total)}</ToolButton>}
        <ToolButton secondary disabled={enviando} onClick={fechar}>{mpPix ? "Cancelar Pix" : "Voltar"}</ToolButton>
      </div>
    </ToolDialog>
  );
}

const consultarPix = dados => httpsCallable(getFunctions(undefined, "us-central1"), "mpConsultarLoja")(dados).then(r => r.data);
const gerarPixRepasse = dados => httpsCallable(getFunctions(undefined, "us-central1"), "mpPixRepasse")(dados).then(r => r.data);
const novoPedido = () => "rep-" + (crypto.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));

// Pagar o repasse ao fornecedor: Pix no Mercado Pago dele (a baixa sai sozinha)
// ou Pix normal pela chave dele (a loja avisa e ele confere e dá a baixa).
function PagarRepasse({ loja, saldo, onClose }) {
  const [valor, setValor] = useState(saldo > 0 ? saldo.toFixed(2).replace(".", ",") : "");
  const [mp, setMp] = useState(null);
  const [qr, setQr] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const v = centavos(num(valor));
  const chave = loja.donoPix?.chave;
  const codigo = mp?.pix?.codigo || (!loja.donoMp && chave && v > 0 ? gerarPixPayload({ chave, nome: loja.donoPix.nome || loja.donoNome, cidade: loja.donoPix.cidade, valor: v }) : "");
  useEffect(() => {
    if (!codigo) { setQr(null); return; }
    let vivo = true;
    import("qrcode").then(Q => Q.toDataURL(codigo, { width: 240, margin: 2, color: { dark: "#000", light: "#fff" } })).then(url => vivo && setQr(url)).catch(() => {});
    return () => { vivo = false; };
  }, [codigo]);
  // Pix do Mercado Pago: confere a cada 5 s até o pagamento confirmar
  useEffect(() => {
    if (!mp?.cobrancaId) return;
    const t = setInterval(() => consultarPix({ cobrancaId: mp.cobrancaId }).then(r => {
      if (r.situacao === "pago") { clearInterval(t); toast("Repasse pago! O fornecedor já recebeu a baixa."); onClose(); }
    }).catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [mp?.cobrancaId, onClose]);
  const gerarMp = async () => {
    setEnviando(true);
    try { setMp(await gerarPixRepasse({ lojaId: loja.id, valor: v, requestId: novoPedido() })); }
    catch (e) { toast(e?.message || "Não foi possível gerar o Pix agora.", "error"); }
    setEnviando(false);
  };
  const avisar = async () => {
    setEnviando(true);
    try {
      await addDoc(collection(db, "lojas_parceiras", loja.id, "avisos"), { valor: v, metodo: "pix", status: "pendente", criadoEm: serverTimestamp() });
      toast("Avisamos o fornecedor. Ele confere o Pix e dá a baixa.");
      onClose();
    } catch { toast("Não foi possível avisar o fornecedor.", "error"); }
    setEnviando(false);
  };
  const copiar = () => navigator.clipboard?.writeText(codigo).then(() => { setCopiado(true); setTimeout(() => setCopiado(false), 2500); }).catch(() => {});

  return (
    <ToolDialog eyebrow="SUA LOJA" title="Pagar repasse" subtitle={"Você deve " + brl(saldo) + " a " + (loja.donoNome || "seu fornecedor") + "."} icon="notas" color="#71e5ce" onClose={onClose} busy={enviando} compact>
      <div className="partner-payment">
        <label className="partner-payment-field">Valor do Pix
          <input inputMode="decimal" disabled={!!mp} value={valor} onChange={e => setValor(e.target.value)} />
        </label>
        {codigo && <>
          <div className="partner-payment-qr">{qr ? <img src={qr} alt={"QR Code Pix de " + brl(v)} /> : <span>Gerando QR Code…</span>}</div>
          <ToolButton secondary onClick={copiar}>{copiado ? "Código copiado!" : "Copiar Pix copia e cola"}</ToolButton>
        </>}
        {loja.donoMp ? (mp
          ? <p className="partner-payment-note" aria-live="polite">Aguardando o pagamento… Assim que o Pix cair, a baixa é feita sozinha e o fornecedor é avisado.</p>
          : <ToolButton disabled={enviando || !(v > 0)} onClick={gerarMp}>{enviando ? "Gerando Pix…" : "Gerar Pix de " + brl(v)}</ToolButton>)
          : <>
            <p className="partner-payment-note">{chave ? "Pague pelo QR Code ou pelo copia e cola. Depois toque em \"Já paguei\": o fornecedor recebe um aviso, confere e dá a baixa." : "O fornecedor ainda não cadastrou uma chave Pix. Combine como pagar e, depois, avise por aqui."}</p>
            <ToolButton disabled={enviando || !(v > 0)} onClick={avisar}>{enviando ? "Avisando…" : "Já paguei " + brl(v)}</ToolButton>
          </>}
        <ToolButton secondary disabled={enviando} onClick={onClose}>Fechar</ToolButton>
      </div>
    </ToolDialog>
  );
}

const SITUACAO_AVISO = { pendente: "aguardando o fornecedor conferir", confirmado: "confirmado", recusado: "o fornecedor não encontrou este Pix", aguardando: "Pix gerado, aguardando pagamento" };

// Status da conexão Mercado Pago da própria loja (só no site, como no painel do vendedor)
function useMercadoPagoDaLoja() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const uid = auth?.currentUser?.uid;
    if (!uid || !mpHabilitado()) return;
    return onSnapshot(doc(db, "users", uid, "mercadopago", MP_AMBIENTE), s => setStatus(s.data() || null), () => setStatus(null));
  }, []);
  return status;
}

const MERCADO_PAGO_LOJA = {
  ...gatewayPorId("mercadopago"),
  resumo: "Pix do PDV com confirmação automática",
  conectar: opcoes => iniciarConexao(opcoes, "mpLojaParceiraIniciar"),
  desconectar: desconectarMercadoPagoLoja,
  antesDeConectar: gatewayPorId("mercadopago").antesDeConectar.map(p => p.icone === "recibo"
    ? { ...p, texto: "Acesso concedido: criar cobranças Pix no PDV e consultar se foram pagas. A venda é registrada quando o pagamento é confirmado." } : p),
  seguranca: gatewayPorId("mercadopago").seguranca.slice(0, 2).concat("O Pix é consultado no Mercado Pago antes de a venda ser registrada no PDV."),
};

function MercadoPagoDaLoja({ status }) {
  const [janela, setJanela] = useState(null);
  const gw = MERCADO_PAGO_LOJA;
  const estado = estadoDoGateway(gw, status);
  const conectado = estado === "conectado";
  const reconectar = estado === "reconectar";
  const conta = gw.conta(status);
  const fechar = () => setJanela(null);
  return (
    <ToolSection title="Mercado Pago da loja" subtitle="Com a sua conta conectada, o Pix do PDV sai pelo Mercado Pago e a venda é registrada sozinha quando o cliente paga.">
      {!mpHabilitado() ? <p className="partner-payment-note">Para usar o Mercado Pago, abra o painel pelo site printcontrol.netlify.app.</p>
        : <div className="pgw">
          <div className="pgw-topo">
            <i className={`pgw-topo-dot ${conectado ? "ok" : reconectar ? "alerta" : ""}`} aria-hidden="true" />
            <div className="pgw-topo-texto"><strong>{conectado ? "Recebendo pelo Mercado Pago" : reconectar ? "Reconexão necessária" : "Mercado Pago não conectado"}</strong>
              <p>{conectado ? ["O dinheiro cai direto na sua conta", conta].filter(Boolean).join(" · ") : reconectar ? "Reconecte sua conta para voltar a receber Pix pelo PDV." : "Conecte sua conta para confirmar os pagamentos automaticamente."}</p>
            </div>
            <ToolButton secondary onClick={() => setJanela(conectado || reconectar ? "gerenciar" : "conectar")}>{conectado || reconectar ? "Gerenciar" : "Conectar Mercado Pago"}</ToolButton>
          </div>
          {gw.emTeste() && <span className="pgw-tag">Ambiente de teste</span>}
          <AlertasMercadoPago uid={auth?.currentUser?.uid} />
        </div>}
      {janela === "conectar" && <FluxoConexao gw={gw} status={status} onClose={fechar} />}
      {janela === "gerenciar" && <GerenciarGateway gw={gw} status={status} estado={estado} onClose={fechar} onReconectar={() => setJanela("conectar")}
        avisoPixManual="O PDV volta a usar a chave Pix da loja, se cadastrada. Você precisa conferir o recebimento e confirmar a venda manualmente." />}
    </ToolSection>
  );
}

// Chave Pix da loja (o QR Code do PDV usa estes dados)
function PixDaLoja({ loja }) {
  const [f, setF] = useState({ chave: loja.pixLoja?.chave || "", nome: loja.pixLoja?.nome || loja.nome || "", cidade: loja.pixLoja?.cidade || "" });
  const [salvando, setSalvando] = useState(false);
  const tipo = tipoChavePix(f.chave);
  const salvar = async e => {
    e.preventDefault();
    if (f.chave.trim() && tipo === "Formato não reconhecido") return toast("Confira a chave Pix: use CPF, CNPJ, celular, e-mail ou chave aleatória.", "error");
    setSalvando(true);
    try {
      await updateDoc(doc(db, "lojas_parceiras", loja.id), { pixLoja: { chave: normalizarChavePix(f.chave), nome: f.nome.trim().slice(0, 25), cidade: f.cidade.trim().slice(0, 15) } });
      toast("Pix da loja salvo.");
    } catch { toast("Não foi possível salvar o Pix.", "error"); }
    setSalvando(false);
  };
  return (
    <ToolSection title="Pix manual da loja" subtitle="Usado no PDV quando o Mercado Pago está desconectado. Confira o recebimento na sua conta antes de confirmar a venda.">
      <form onSubmit={salvar} className="partner-pix-form">
        <Inp label={"Chave Pix" + (tipo ? " · " + tipo : "")} style={{ minHeight: 48, fontSize: 16 }} value={f.chave} onChange={e => setF({ ...f, chave: e.target.value })} placeholder="CPF, CNPJ, celular, e-mail ou chave aleatória" />
        <Inp label="Nome do recebedor" style={{ minHeight: 48, fontSize: 16 }} maxLength={25} value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} />
        <Inp label="Cidade" style={{ minHeight: 48, fontSize: 16 }} maxLength={15} value={f.cidade} onChange={e => setF({ ...f, cidade: e.target.value })} />
        <ToolButton type="submit" disabled={salvando}>{salvando ? "Salvando…" : "Salvar Pix"}</ToolButton>
      </form>
    </ToolSection>
  );
}

// A loja não tem users/{uid}: o vínculo é o e-mail (confirmado) cadastrado pelo dono.
export async function buscarLojaParceira(email) {
  if (!email) return null;
  try {
    const snap = await getDocs(query(collection(db, "lojas_parceiras"), where("email", "==", email.trim().toLowerCase()), limit(1)));
    return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
  } catch { return null; }
}

// Conta sem cadastro de vendedor (users/{uid}) cujo e-mail é de uma loja parceira.
export async function lojaDaConta(user) {
  try {
    const perfil = await getDoc(doc(db, "users", user.uid));
    if (perfil?.exists() && perfil.data().cadastroCompleto !== false) return null;
  } catch { /* sem perfil legível: confere a loja mesmo assim */ }
  return buscarLojaParceira(user.email);
}

export function useLojaParceira(lojaId) {
  const [dados, setDados] = useState({ loja: null, semAcesso: false, itens: [], vendas: [], repasses: [], avisos: [] });
  useEffect(() => {
    if (!lojaId) return;
    const base = `lojas_parceiras/${lojaId}`;
    const lista = campo => snap => setDados(d => ({ ...d, [campo]: snap.docs.map(x => ({ id: x.id, ...x.data() })) }));
    const erro = e => console.error("loja parceira", e);
    const subs = [
      onSnapshot(doc(db, base), s => setDados(d => ({ ...d, loja: s.exists() ? { id: s.id, ...s.data() } : null, semAcesso: !s.exists() })), () => setDados(d => ({ ...d, semAcesso: true }))),
      onSnapshot(collection(db, base, "itens"), lista("itens"), erro),
      onSnapshot(query(collection(db, base, "vendas"), orderBy("criadoEm", "desc")), lista("vendas"), erro),
      onSnapshot(query(collection(db, base, "repasses"), orderBy("criadoEm", "desc")), lista("repasses"), erro),
      onSnapshot(query(collection(db, base, "avisos"), orderBy("criadoEm", "desc")), lista("avisos"), erro),
    ];
    return () => subs.forEach(u => u());
  }, [lojaId]);
  return dados;
}

// Logo de um dos lados da parceria; sem imagem, mostra a inicial do nome.
function LogoParceiro({ src, nome }) {
  const [falhou, setFalhou] = useState(null);
  return <span className="pc-company-mark partner-logo">{src && falhou !== src ? <img src={src} alt="" onError={() => setFalhou(src)} /> : <b>{(nome || "?").trim().charAt(0).toUpperCase()}</b>}</span>;
}

// Topo da parceria: logo do dono 🤝 logo da loja (usado no painel da loja e na aba do dono).
export function MarcaParceria({ loja, subtitulo }) {
  const dono = loja?.donoNome || "Fornecedor", nome = loja?.nome || "Loja parceira";
  return (
    <div className="pc-company partner-brand">
      <div className="partner-logos" role="img" aria-label={`Parceria entre ${dono} e ${nome}`}>
        <LogoParceiro src={loja?.donoLogo} nome={dono} />
        <span className="partner-handshake" aria-hidden="true">🤝</span>
        <LogoParceiro src={loja?.logoLoja} nome={nome} />
      </div>
      <div className="pc-company-copy">
        <span className="pc-company-eyebrow">LOJA PARCEIRA</span>
        <strong title={nome}>{nome}</strong>
        <span className="pc-company-subtitle">{subtitulo || `Parceria com ${dono}`}</span>
      </div>
    </div>
  );
}

// Logo enviada pela loja: reduzida no aparelho (até 256 px) e guardada no
// próprio documento da loja, sem Storage. A regra limita o tamanho.
async function reduzirLogo(arquivo, lado = 256) {
  if (!arquivo.type.startsWith("image/")) throw new Error("Escolha um arquivo de imagem.");
  const url = URL.createObjectURL(arquivo);
  try {
    const img = await new Promise((ok, erro) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => erro(new Error("Não foi possível abrir a imagem.")); i.src = url; });
    const escala = Math.min(1, lado / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(img.width * escala));
    canvas.height = Math.max(1, Math.round(img.height * escala));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const dados = canvas.toDataURL("image/webp", 0.9);
    if (dados.length > 190000) throw new Error("Imagem grande demais. Tente outra.");
    return dados;
  } finally { URL.revokeObjectURL(url); }
}

function LogoDaLoja({ loja }) {
  const [salvando, setSalvando] = useState(false);
  const salvar = async logoLoja => {
    setSalvando(true);
    try {
      await updateDoc(doc(db, "lojas_parceiras", loja.id), { logoLoja });
      toast(logoLoja ? "Logo salva." : "Logo removida.");
    } catch { toast("Não foi possível salvar a logo.", "error"); }
    setSalvando(false);
  };
  const escolher = async e => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    if (!arquivo) return;
    try { await salvar(await reduzirLogo(arquivo)); } catch (err) { toast(err.message, "error"); }
  };
  return (
    <ToolSection title="Logo da sua loja" subtitle="Aparece no topo, ao lado da logo do fornecedor.">
      <div className="partner-logo-edit">
        <LogoParceiro src={loja.logoLoja} nome={loja.nome} />
        <div className="tool-actions">
          <label className={`tool-button${salvando ? " is-disabled" : ""}`}>
            {salvando ? "Salvando…" : loja.logoLoja ? "Trocar logo" : "Enviar logo"}
            <input type="file" accept="image/*" hidden disabled={salvando} onChange={escolher} />
          </label>
          {loja.logoLoja && <ToolButton secondary disabled={salvando} onClick={() => salvar("")}>Remover</ToolButton>}
        </div>
      </div>
    </ToolSection>
  );
}

export function Numero({ titulo, valor, cor = C.text, dica }) {
  return (
    <Card className="partner-number" style={{ padding: 14, flex: "1 1 140px", minWidth: 0 }}>
      <div style={{ color: C.muted, fontSize: 12, fontWeight: 600 }}>{titulo}</div>
      <div style={{ color: cor, fontSize: 20, fontWeight: 800, marginTop: 4 }}>{valor}</div>
      {dica && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{dica}</div>}
    </Card>
  );
}

export function ListaVendas({ vendas, mostrarLucro = true, limite = 50 }) {
  if (!vendas.length) return <p style={{ color: C.muted, fontSize: 13 }}>Nenhuma venda ainda.</p>;
  return (
    <div className="partner-sales-list">
      {vendas.slice(0, limite).map(v => (
        <article className="partner-sale" key={v.id} aria-label={`Venda de ${quando(v.criadoEm)}`}>
          <header className="partner-sale-header"><strong>{quando(v.criadoEm)}</strong><span>{PAGAMENTOS.find(([id]) => id === v.formaPagamento)?.[1] || v.formaPagamento || "Não informado"}</span></header>
          <ul className="partner-sale-products">
            {(v.itens || []).map((i, index) => <li key={i.id || index}><span className="partner-sale-quantity">{i.qtd} un.</span><strong>{i.nome || "Produto"}</strong></li>)}
          </ul>
          <dl className={`partner-sale-totals${mostrarLucro ? "" : " partner-sale-totals--supplier"}`}>
            <div><dt>Total da venda</dt><dd>{brl(v.total)}</dd></div>
            <div><dt>Repasse</dt><dd>{brl(v.totalRepasse)}</dd></div>
            {mostrarLucro && <div><dt>Seu lucro</dt><dd className="partner-sale-profit">{brl(v.lucroLoja)}</dd></div>}
          </dl>
        </article>
      ))}
    </div>
  );
}

const dataInput = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const textoBusca = s => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function HistoricoVendas({ vendas }) {
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const filtrosId = useId();
  const [periodo, setPeriodo] = useState("mes");
  const [referencia, setReferencia] = useState(() => dataInput(new Date()));
  const [busca, setBusca] = useState("");
  const [limite, setLimite] = useState(50);
  const { inicio, fim, vendas: doPeriodo } = vendasNoPeriodo(vendas, periodo, new Date(referencia + "T12:00:00"));
  const filtradas = doPeriodo.filter(v => textoBusca([...(v.itens || []).map(i => i.nome), v.formaPagamento, PAGAMENTOS.find(([id]) => id === v.formaPagamento)?.[1]].join(" ")).includes(textoBusca(busca.trim())));
  const resumo = resumoLoja({ vendas: filtradas });
  const ultimo = new Date(fim); ultimo.setDate(ultimo.getDate() - 1);
  const rotulo = periodo === "mes" ? inicio.toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
    : periodo === "semana" ? `${inicio.toLocaleDateString("pt-BR")} a ${ultimo.toLocaleDateString("pt-BR")}`
    : inicio.toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" });
  function mudarData(valor) { if (valor) { setReferencia(valor); setLimite(50); } }
  function navegar(delta) {
    const proxima = new Date(inicio);
    if (periodo === "mes") proxima.setMonth(proxima.getMonth() + delta);
    else proxima.setDate(proxima.getDate() + delta * (periodo === "semana" ? 7 : 1));
    mudarData(dataInput(proxima));
  }
  return <ToolSection title="Histórico de vendas" subtitle="Produtos, pagamentos e valores de cada venda." aria-label="Histórico de vendas">
    <div className="partner-history">
      <ToolSearch value={busca} onChange={valor => { setBusca(valor); setLimite(50); }} placeholder="Pesquisar produto ou pagamento">
        <ToolButton secondary icon="config" aria-expanded={filtrosAbertos} aria-controls={filtrosId} onClick={() => setFiltrosAbertos(abertos => !abertos)}>Filtros</ToolButton>
      </ToolSearch>
      <p className="partner-history-active-period" aria-live="polite">{periodo === "mes" ? "Mês" : periodo === "semana" ? "Semana" : "Dia"}: <strong>{rotulo}</strong></p>
      <div className="partner-history-filters" id={filtrosId} hidden={!filtrosAbertos}>
        <ToolFilters options={[{ value: "dia", label: "Dia" }, { value: "semana", label: "Semana" }, { value: "mes", label: "Mês" }]} value={periodo} onChange={valor => { setPeriodo(valor); setLimite(50); }} />
        <div className="partner-history-period">
          <ToolButton secondary aria-label="Período anterior" onClick={() => navegar(-1)}>←</ToolButton>
          <strong>{rotulo}</strong>
          <ToolButton secondary aria-label="Próximo período" onClick={() => navegar(1)}>→</ToolButton>
        </div>
        <div className="partner-history-date"><label htmlFor="partner-history-date">Data de referência<input id="partner-history-date" type="date" value={referencia} onChange={e => mudarData(e.target.value)} /></label><ToolButton secondary onClick={() => mudarData(dataInput(new Date()))}>Hoje</ToolButton></div>
        <p className="partner-history-filter-note">A semana começa na segunda-feira. Os filtros são aplicados automaticamente.</p>
      </div>
      <div className="partner-history-summary"><Numero titulo="Vendas encontradas" valor={filtradas.length} /><Numero titulo="Total vendido" valor={brl(resumo.vendido)} /><Numero titulo="Lucro no período" valor={brl(resumo.lucroLoja)} cor={C.success} /></div>
      {filtradas.length ? <ListaVendas vendas={filtradas} limite={limite} /> : <ToolEmpty icon="busca" title={busca.trim() ? "Nenhuma venda encontrada" : "Nenhuma venda neste período"}>Escolha outro período{busca.trim() ? " ou ajuste a pesquisa" : " para consultar o histórico"}.</ToolEmpty>}
      {filtradas.length > limite && <ToolButton secondary onClick={() => setLimite(l => l + 50)}>Mostrar mais vendas ({filtradas.length - limite} restantes)</ToolButton>}
    </div>
  </ToolSection>;
}

function Pdv({ lojaId, loja, itens, mp }) {
  const [carrinho, setCarrinho] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [pagando, setPagando] = useState(null);
  const disponiveis = itens.filter(i => (Number(i.quantidade) || 0) > 0);
  const linhas = Object.entries(carrinho).map(([id, qtd]) => ({ item: itens.find(i => i.id === id), qtd })).filter(l => l.item);
  const total = centavos(linhas.reduce((t, l) => t + l.qtd * precoDaLoja(l.item), 0));
  const lucro = centavos(total - linhas.reduce((t, l) => t + l.qtd * (Number(l.item.repasse) || 0), 0));
  const mudar = (item, delta) => setCarrinho(c => {
    const qtd = Math.min(Number(item.quantidade) || 0, (c[item.id] || 0) + delta);
    const { [item.id]: _, ...resto } = c;
    return qtd > 0 ? { ...resto, [item.id]: qtd } : resto;
  });
  const pagoMp = useCallback(() => { setCarrinho({}); setPagando(null); toast("Pix confirmado pelo Mercado Pago. Venda registrada!"); }, []);
  const vender = async forma => {
    if (!linhas.length || enviando) return;
    setEnviando(true);
    try {
      const { data } = await registrarVenda({ lojaId, formaPagamento: forma, itens: linhas.map(l => ({ id: l.item.id, qtd: l.qtd })) });
      setCarrinho({});
      setPagando(null);
      toast(`Venda registrada: ${brl(data.total)} · seu lucro ${brl(data.lucroLoja)}`, "success");
    } catch (e) {
      toast(e?.message || "Não foi possível registrar a venda. Confira a conexão.", "error");
    }
    setEnviando(false);
  };

  return (
    <div className="partner-pdv">
      {pagando && <Pagamento forma={pagando} total={total} loja={loja} enviando={enviando} onConfirmar={() => vender(pagando)} onClose={() => setPagando(null)}
        mp={mp} itensVenda={linhas.map(l => ({ id: l.item.id, qtd: l.qtd }))} onPagoMp={pagoMp} />}
      <section className="partner-catalog" aria-label="Produtos disponíveis">
        <div className="partner-section-heading"><h2>Produtos disponíveis</h2><span>{disponiveis.length} produtos</span></div>
        {!disponiveis.length && <ToolEmpty icon="cube" title="Seu estoque está vazio">Fale com o fornecedor para receber peças e começar a vender.</ToolEmpty>}
        <div className="partner-products">
        {disponiveis.map(i => (
          <button key={i.id} type="button" className={`partner-product${carrinho[i.id] ? " is-selected" : ""}`} aria-label={`Adicionar ${i.nome} ao carrinho`} disabled={enviando || (carrinho[i.id] || 0) >= Number(i.quantidade)} onClick={() => mudar(i, 1)}>
            <div className="partner-product-photo">
              {i.foto ? <img src={i.foto} alt="" /> : <HeaderIcon name="cube" size={42} />}
              {!!carrinho[i.id] && <span className="partner-product-badge">{carrinho[i.id]} no carrinho</span>}
            </div>
            <div className="partner-product-copy"><strong>{i.nome}</strong><span className="partner-product-price">{brl(precoDaLoja(i))}</span><div className="partner-product-footer"><small>{i.quantidade} em estoque</small><CalcIcon name="plus" /></div></div>
          </button>
        ))}
        </div>
      </section>
      <aside className="partner-cart" aria-label="Carrinho da venda" aria-busy={enviando}>
        <div className="partner-section-heading"><h2><CalcIcon name="receipt" size={20} /> Venda atual</h2><span>{linhas.reduce((n, l) => n + l.qtd, 0)} peças</span></div>
        {!linhas.length && <div className="partner-cart-empty"><CalcIcon name="receipt" size={32} /><strong>Vamos fazer uma venda?</strong><p>Toque em um produto para adicioná-lo ao carrinho.</p></div>}
        <div className="partner-cart-lines">
        {linhas.map(({ item, qtd }) => (
          <div key={item.id} className="partner-cart-line">
            <div><strong>{item.nome}</strong><small>{brl(qtd * precoDaLoja(item))}</small></div>
            <div className="partner-quantity"><button type="button" disabled={enviando} aria-label={`Tirar um ${item.nome}`} onClick={() => mudar(item, -1)}>−</button><span>{qtd}</span><button type="button" disabled={enviando || qtd >= Number(item.quantidade)} aria-label={`Mais um ${item.nome}`} onClick={() => mudar(item, 1)}>+</button></div>
          </div>
        ))}
        </div>
        <div className="partner-cart-total" aria-live="polite">
          <span>Total da venda</span><strong>{brl(total)}</strong>
        </div>
        {linhas.length > 0 && <div className="partner-cart-profit">Seu lucro <strong>{brl(lucro)}</strong></div>}
        <p className="partner-payment-label">{enviando ? "Registrando venda…" : "Recebeu? Finalize com a forma de pagamento"}</p>
        <div className="partner-payments">
          {PAGAMENTOS.map(([id, rotulo]) => <ToolButton key={id} disabled={!linhas.length || enviando} onClick={() => setPagando(id)}>{rotulo}</ToolButton>)}
        </div>
      </aside>
    </div>
  );
}

function Estoque({ lojaId, itens }) {
  const [precos, setPrecos] = useState({});
  const salvar = async item => {
    const valor = centavos(String(precos[item.id] ?? "").replace(",", "."));
    if (!(valor > 0)) return setPrecos(p => ({ ...p, [item.id]: undefined }));
    if (valor > item.precoMaximo) { toast(`O máximo para ${item.nome} é ${brl(item.precoMaximo)}.`, "error"); return setPrecos(p => ({ ...p, [item.id]: undefined })); }
    try {
      await updateDoc(doc(db, "lojas_parceiras", lojaId, "itens", item.id), { precoVenda: valor });
      toast("Preço salvo.", "success");
    } catch { toast("Não foi possível salvar o preço.", "error"); }
    setPrecos(p => ({ ...p, [item.id]: undefined }));
  };
  if (!itens.length) return <ToolEmpty icon="cube" title="Nenhuma peça recebida">Seu fornecedor ainda não deixou peças com você.</ToolEmpty>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ color: C.muted, fontSize: 12, margin: 0 }}>Você escolhe seu preço até o máximo do fornecedor. O que passar do repasse é o seu lucro.</p>
      {itens.map(i => {
        const preco = precoDaLoja(i);
        return (
          <Card className="partner-stock-row" key={i.id} style={{ padding: 12, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
            <div className="partner-stock-photo">{i.foto ? <img src={i.foto} alt="" /> : <HeaderIcon name="cube" size={24} />}</div>
            <div style={{ flex: "1 1 160px", minWidth: 0 }}>
              <div style={{ color: C.text, fontWeight: 700 }}>{i.nome}</div>
              <div style={{ color: C.muted, fontSize: 12 }}>{i.quantidade} em estoque · {i.vendidos || 0} vendidos</div>
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>Repasse<br /><strong style={{ color: C.text }}>{brl(i.repasse)}</strong></div>
            <div style={{ fontSize: 12, color: C.muted }}>Máximo<br /><strong style={{ color: C.text }}>{brl(i.precoMaximo)}</strong></div>
            <label style={{ fontSize: 12, color: C.muted, display: "flex", flexDirection: "column", gap: 2 }}>Seu preço
              <input inputMode="decimal" value={precos[i.id] ?? preco.toFixed(2).replace(".", ",")} onChange={e => setPrecos(p => ({ ...p, [i.id]: e.target.value }))} onBlur={() => precos[i.id] !== undefined && salvar(i)}
                style={{ width: 90, background: C.panel, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "6px 8px" }} />
            </label>
            <div style={{ fontSize: 12, color: preco < i.repasse ? C.danger : C.success }}>Lucro/peça<br /><strong>{brl(preco - i.repasse)}</strong></div>
          </Card>
        );
      })}
    </div>
  );
}

function Contas({ loja, itens, vendas, repasses, avisos }) {
  const r = resumoLoja({ itens, vendas, repasses });
  const [pagando, setPagando] = useState(false);
  const fechar = useCallback(() => setPagando(false), []);
  const visiveis = avisos.filter(a => a.status !== "aguardando" || a.mp_cobranca?.situacao === "aguardando").slice(0, 10);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <Numero titulo="Vendido" valor={brl(r.vendido)} />
        <Numero titulo="Seu lucro" valor={brl(r.lucroLoja)} cor={C.success} />
        <Numero titulo="A repassar" valor={brl(r.saldo)} cor={r.saldo > 0 ? C.warning : C.text} dica="Combine o acerto com o fornecedor" />
        <Numero titulo="Já repassado" valor={brl(r.recebido)} />
      </div>
      {pagando && <PagarRepasse loja={loja} saldo={r.saldo} onClose={fechar} />}
      <div className="tool-actions"><ToolButton disabled={!(r.saldo > 0)} onClick={() => setPagando(true)}>Pagar repasse por Pix</ToolButton></div>
      {visiveis.length > 0 && <ToolSection title="Pix enviados ao fornecedor">
        {visiveis.map(a => <div key={a.id} style={{ color: C.muted, fontSize: 13, padding: "4px 0" }}>{quando(a.criadoEm)} — <strong style={{ color: C.text }}>{brl(a.valor)}</strong> · {SITUACAO_AVISO[a.status] || a.status}</div>)}
      </ToolSection>}
      <HistoricoVendas vendas={vendas} />
      {repasses.length > 0 && <ToolSection title="Repasses registrados pelo fornecedor">
        {repasses.map(p => <div key={p.id} style={{ color: C.muted, fontSize: 13, padding: "4px 0" }}>{quando(p.criadoEm)} — <strong style={{ color: C.text }}>{brl(p.valor)}</strong>{p.obs ? ` · ${p.obs}` : ""}</div>)}
      </ToolSection>}
    </div>
  );
}

const ABAS = [["pdv", "PDV", "receipt"], ["estoque", "Estoque", "box"], ["contas", "Contas", "card"], ["config", "Configurações", "config"]];
const TITULOS = {
  pdv: ["Hora de vender", "Escolha as peças, monte o carrinho e registre a venda."],
  estoque: ["Seu estoque", "Acompanhe as peças recebidas e ajuste seu preço de venda."],
  contas: ["Suas contas", "Vendas, lucro e acertos com o fornecedor em um só lugar."],
  config: ["Configurações da loja", "Organize os pagamentos, os dados da parceria e o acesso à sua conta."],
};

export default function PainelLojaParceira({ lojaId, email, onLogout }) {
  const { loja, semAcesso, itens, vendas, repasses, avisos } = useLojaParceira(lojaId);
  const [aba, setAba] = useState("pdv");
  const statusMp = useMercadoPagoDaLoja();
  const mpConectado = mpHabilitado() && !!statusMp?.conectado;
  // Volta da autorização do Mercado Pago: conclui a conexão da loja aqui
  useEffect(() => {
    const retorno = mpHabilitado() ? retornoPendente() : null;
    if (!retorno) return;
    setAba("config");
    concluirConexao(retorno, "mpLojaParceiraConcluir")
      .then(() => toast("Mercado Pago conectado! O Pix do PDV agora confirma sozinho."))
      .catch(e => toast(mensagemMP(e), "error"));
  }, []);
  const ordenados = useMemo(() => [...itens].sort((a, b) => (a.nome || "").localeCompare(b.nome || "")), [itens]);
  const resumo = resumoLoja({ itens, vendas, repasses });

  if (semAcesso) return (
    <div style={{ minHeight: "100dvh", background: C.bg, display: "grid", placeItems: "center", padding: 24 }}>
      <Card style={{ maxWidth: 360, textAlign: "center", display: "flex", flexDirection: "column", gap: 12 }}>
        <HeaderIcon name="cube" size={40} />
        <strong style={{ color: C.text }}>Esta loja não está mais ativa</strong>
        <span style={{ color: C.muted, fontSize: 13 }}>O fornecedor encerrou a parceria. Fale com ele se achar que é engano.</span>
        <Btn outline onClick={onLogout}>Sair da conta</Btn>
      </Card>
    </div>
  );

  return (
    <div className="partner-workspace">
      <ToastContainer />
      <header className="pc-workspace-header">
        <div className="pc-workspace-top partner-header-inner">
          <MarcaParceria loja={loja} />
          <div className="partner-account"><span className="partner-access-label">ACESSO DA LOJA</span><LogoutButton onClick={onLogout} /></div>
        </div>
      </header>
      <main className="partner-main">
      <nav aria-label="Abas da loja" className="partner-tabs">
        {ABAS.map(([id, rotulo, icon]) => (
          <button key={id} type="button" aria-current={aba === id ? "page" : undefined} onClick={() => setAba(id)}
            >{id === "config" ? <HeaderIcon name={icon} size={19} /> : <CalcIcon name={icon} size={19} />}<span>{rotulo}</span></button>
        ))}
      </nav>
      <div className="partner-page-heading"><span className="tool-eyebrow">SUA LOJA / {ABAS.find(([id]) => id === aba)[1].toUpperCase()}</span><h1>{TITULOS[aba][0]}</h1><p>{TITULOS[aba][1]}</p></div>
        {!loja ? <p role="status" className="partner-loading">Carregando sua loja…</p> : <>
        {aba === "pdv" && <div className="partner-overview"><Numero titulo="Peças em estoque" valor={resumo.pecas} dica="Prontas para vender" /><Numero titulo="Seu lucro" valor={brl(resumo.lucroLoja)} cor={C.success} dica="Acumulado nas vendas" /><Numero titulo="A repassar" valor={brl(resumo.saldo)} cor={resumo.saldo > 0 ? C.warning : C.text} dica="Acerto com o fornecedor" /></div>}
        {aba === "pdv" && <Pdv lojaId={lojaId} loja={loja} itens={ordenados} mp={mpConectado} />}
        {aba === "estoque" && <Estoque lojaId={lojaId} itens={ordenados} />}
        {aba === "contas" && <Contas loja={loja} itens={itens} vendas={vendas} repasses={repasses} avisos={avisos} />}
        {aba === "config" && (
          <div className="partner-settings">
            <CommerceTabs>
              <CommercePanel key="pagamentos" label="Pagamentos">
                <MercadoPagoDaLoja status={statusMp} />
                <PixDaLoja key={loja.id} loja={loja} />
              </CommercePanel>
              <CommercePanel key="loja" label="Loja e parceria">
                <LogoDaLoja loja={loja} />
                <ToolSection title="Dados da parceria" subtitle="Sua loja conectada ao fornecedor.">
                  <dl className="partner-details"><div><dt>Loja</dt><dd>{loja.nome}</dd></div><div><dt>Fornecedor</dt><dd>{loja.donoNome}</dd></div></dl>
                </ToolSection>
              </CommercePanel>
              <CommercePanel key="acesso" label="Acesso">
                <ToolSection title="Acesso à conta" subtitle="E-mail e senha usados para entrar no painel da loja.">
                  <dl className="partner-details"><div><dt>E-mail de acesso</dt><dd>{email}</dd></div></dl>
                  <p className="partner-account-note">Para trocar a senha, saia e use "Esqueceu a senha?" na tela de entrada.</p>
                  <ToolButton danger onClick={onLogout}>Sair da conta</ToolButton>
                </ToolSection>
              </CommercePanel>
            </CommerceTabs>
          </div>
        )}
        </>}
      </main>
    </div>
  );
}
